import _ from 'lodash';
import {
  DolphinQaStats,
  ManagerStats,
} from 'react-scale/components/ExpertManagement/ExpertManagementPage/types';

type CountCardsProps = { countAttempts: number; countReviews: number };
export const countCards = (current: CountCardsProps, previous: CountCardsProps) => [
  {
    title: 'Attempts',
    subtitle: 'Completed Attempts',
    value: current.countAttempts,
    previous: previous.countAttempts - previous.countAttempts,
  },
  {
    title: 'Reviews',
    subtitle: 'Completed Reviews',
    value: current.countReviews,
    previous: previous.countReviews - previous.countReviews,
  },
];

export const communityCards = (current: ManagerStats, previous: ManagerStats) => [
  {
    title: 'Community',
    subtitle: 'Team Size',
    value: current.teamSize,
  },
  {
    title: 'Community',
    subtitle: 'Team Activity',
    value: `${
      current.teamSize > 0 ? Math.round((100 * current.numActive) / current.teamSize) : 0
    }%`,
  },
  ...(current.slackJoinPercentage != null
    ? [
        {
          title: 'Community',
          subtitle: 'Slack Join %',
          value: current.slackJoinPercentage != null ? `${current.slackJoinPercentage}%` : '-',
        },
      ]
    : []),
  {
    title: 'Tasks',
    subtitle: 'Total Tasks',
    value: current.teamTasks,
    delta: current.teamTasks - previous.teamTasks,
  },
  {
    title: 'Reviews',
    subtitle: 'Total Reviews',
    value: current.teamReviews,
    delta: current.teamReviews - previous.teamReviews,
  },
];

export const dolphinCards = (
  current: DolphinQaStats | undefined,
  previous: DolphinQaStats | undefined,
) => [
  {
    title: 'Dolphin',
    subtitle: 'QA Count',
    value: current?.count ?? '-',
    delta: current?.count && previous?.count ? current?.count - previous?.count : undefined,
  },
  {
    title: 'Dolphin',
    subtitle: 'Avg Prompt Quality',
    value: current?.averagePromptQualityScore
      ? `${Math.round(100 * current.averagePromptQualityScore) / 100}`
      : '-',
    delta:
      current?.averagePromptQualityScore && previous?.averagePromptQualityScore
        ? Math.round(
            (current?.averagePromptQualityScore - previous?.averagePromptQualityScore) * 100,
          ) / 100
        : undefined,
  },
  {
    title: 'Dolphin',
    subtitle: 'Avg Resp Agreement',
    value: current?.averageResponseAgreement
      ? `${Math.round(current.averageResponseAgreement)}%`
      : '-',
    delta:
      current?.averageResponseAgreement && previous?.averageResponseAgreement
        ? Math.round(current?.averageResponseAgreement - previous?.averageResponseAgreement)
        : undefined,
  },
  {
    title: 'Dolphin',
    subtitle: 'Avg SI Agreement',
    value: current?.averageSystemInstructionsAgreement
      ? `${Math.round(current.averageSystemInstructionsAgreement)}%`
      : '-',
    delta:
      current?.averageSystemInstructionsAgreement && previous?.averageSystemInstructionsAgreement
        ? Math.round(
            current?.averageSystemInstructionsAgreement -
              previous?.averageSystemInstructionsAgreement,
          )
        : undefined,
  },
];

type QualityCardsProps = {
  qmsScore: number | null;
  averageSbqPercentage: number | null;
  auditCoverage?: number;
  averageFractalGuardian: number | null;
  gqaAndSpeedAuditScore: number | null;
  speedAuditScore: number;
  speedAuditPdr: number | null;
  speedAuditCount: number | null;
  qualityControlScore: number;
  qualityControlPdr: number | null;
  qualityControlCount: number | null;
};

/** Safely, calculate the difference between the current data & previous data.
 * Will be used to show improvement or regression in a user's data */
const computeDifference = (current: number | null, previous: number | null): number | undefined => {
  if (_.isNil(current) || _.isNil(previous)) {
    return undefined;
  }
  return _.round(current - previous, 2);
};

export function qualityCards(current: QualityCardsProps, previous: QualityCardsProps) {
  const cards = [
    {
      title: 'Quality',
      subtitle: 'QMS Score (weighted by number of reviews)',
      value: !_.isNil(current.qmsScore) ? _.round(current.qmsScore, 2) : '-',
      delta: computeDifference(current.qmsScore, previous.qmsScore),
    },
    {
      title: 'Quality',
      subtitle: 'SBQ %',
      value: !_.isNil(current.averageSbqPercentage)
        ? _.round(current.averageSbqPercentage, 1)
        : '-',
      delta: computeDifference(current.averageSbqPercentage, previous.averageSbqPercentage),
    },
    ...(!_.isNil(current.averageFractalGuardian)
      ? [
          {
            title: 'Quality',
            subtitle: 'Fractal Guardian',
            value: _.round(current.averageFractalGuardian, 1),
            delta: computeDifference(
              current.averageFractalGuardian,
              previous.averageFractalGuardian,
            ),
          },
        ]
      : []),
    {
      title: 'Quality',
      subtitle: 'Quality Control Score (avg per user)',
      value: !_.isNil(current.qualityControlScore) ? _.round(current.qualityControlScore, 2) : '-',
      delta: computeDifference(current.qualityControlScore, previous.qualityControlScore),
    },
    {
      title: 'Quality',
      subtitle: 'Quality Control PDR (avg per user)',
      value: !_.isNil(current.qualityControlPdr)
        ? `${_.round(current.qualityControlPdr * 100)}%`
        : '-',
      delta: computeDifference(current.qualityControlPdr, previous.qualityControlPdr),
    },
    {
      title: 'Quality',
      subtitle: 'Users with at least 1 QC Audit Score',
      value: current.qualityControlCount ?? '-',
      delta: computeDifference(current.qualityControlCount, previous.qualityControlCount),
    },
    {
      title: 'Quality',
      subtitle: 'Internal Audit Score (avg per user)',
      value: !_.isNil(current.speedAuditScore) ? _.round(current.speedAuditScore, 2) : '-',
      delta: computeDifference(current.speedAuditScore, previous.speedAuditScore),
    },
    {
      title: 'Quality',
      subtitle: 'Internal Audit PDR (avg per user)',
      value: !_.isNil(current.speedAuditPdr) ? `${_.round(current.speedAuditPdr * 100)}%` : '-',
      delta: computeDifference(current.speedAuditPdr, previous.speedAuditPdr),
    },
    {
      title: 'Quality',
      subtitle: 'Users with at least 1 Internal Audit Score',
      value: current.speedAuditCount ?? '-',
      delta: computeDifference(current.speedAuditCount, previous.speedAuditCount),
    },

    ...(!_.isNil(current.auditCoverage)
      ? [
          {
            title: 'Quality',
            subtitle: 'Internal Audit Coverage',
            value: `${_.round(current.auditCoverage)}%`,
          },
        ]
      : []),

    {
      title: 'Quality',
      subtitle: 'QC & Internal Audit Score (avg per user)',
      value: !_.isNil(current.gqaAndSpeedAuditScore)
        ? _.round(current.gqaAndSpeedAuditScore, 2)
        : '-',
      delta: computeDifference(current.gqaAndSpeedAuditScore, previous.gqaAndSpeedAuditScore),
    },
  ];

  return cards;
}

type BenchmarkCardsProps = {
  averageBmAccuracy: number | undefined;
};
export const benchmarkCards = (current: BenchmarkCardsProps, previous: BenchmarkCardsProps) => [
  {
    title: 'Quality',
    subtitle: 'Avg BM Accuracy',
    value:
      current.averageBmAccuracy !== undefined && current.averageBmAccuracy !== null
        ? `${Math.round(current.averageBmAccuracy)}%`
        : '-',
    delta:
      current.averageBmAccuracy && previous.averageBmAccuracy
        ? Math.round(current.averageBmAccuracy - previous.averageBmAccuracy)
        : undefined,
  },
];

export const communityGlobalStatsCards = (globalStats?: Record<string, number>) =>
  globalStats
    ? [
        {
          className: 'w-[240px]',
          title: 'Active Contributors',
          subtitle: 'Total number of active experts',
          value: globalStats.activeContributorsSize,
        },
        {
          className: 'w-[240px]',
          title: 'Total Community Size',
          subtitle: 'Number of active contributors in communities',
          value: globalStats.communitySize,
        },
        {
          className: 'w-[240px]',
          title: 'Slack Join %',
          subtitle: 'Percentage of contributors in communities that have joined slack',
          value: Math.round(globalStats.slackJoinPercentage * 10) / 10,
          valueSuffix: '%',
        },
        {
          className: 'w-[420px]',
          title: 'New Contributor Community Penetration Rate',
          subtitle:
            'Contributors that joined <3 weeks ago that are in a community as opposed to not in a community',
          value: Math.round(globalStats.ratioNewContributorsInCommunities * 1000) / 10,
          valueSuffix: '%',
        },
        {
          className: 'w-[420px]',
          title: 'Tenured Contributor Community Penetration Rate',
          subtitle:
            'Contributors that joined >3 weeks that are in a community as opposed to not in a community',
          value: Math.round(globalStats.ratioTenuredContributorsInCommunities * 1000) / 10,
          valueSuffix: '%',
        },
        {
          className: 'w-[240px]',
          title: 'Avg Quality Score',
          subtitle: 'over last 2 weeks (all communities)',
          value: Math.round(globalStats.averageQualityScore * 100) / 100,
          valueSuffix: '/5',
        },
      ]
    : [];

export const podStatsCards = (data?: Record<string, number>) =>
  data
    ? [
        {
          className: 'w-[200px]',
          title: 'Community Size',
          subtitle: '',
          value: data.communitySize,
        },
        {
          className: 'w-[200px]',
          title: 'Slack Join %',
          subtitle: '',
          value: Math.round(data.slackJoinPercentage * 10) / 10,
          valueSuffix: '%',
        },
        {
          className: 'w-[200px]',
          title: 'Avg Quality Score',
          subtitle: 'over last 2 weeks',
          value: Math.round(data.avgQualityScore * 10) / 10,
          valueSuffix: '/5',
        },
        {
          className: 'w-[200px]',
          title: 'Active Contributor %',
          subtitle: 'within the last 3 weeks',
          value: Math.round(data.activeContributorPercentage * 1000) / 10,
          valueSuffix: '%',
        },
      ]
    : [];
